<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import _ from 'lodash'
import FormSwitch from '../../components/Atoms/FormSwitch.vue'
import TabGroup from '../../components/Atoms/TabGroup.vue'
import TabItem from '../../components/Atoms/TabItem.vue'
import subscribePlansCompareDescription from './subscribePlansCompareDescription'
import subscribePlansOttCompareDescription from './subscribePlansOttCompareDescription'
import SubscribePlanItemCompareRow from './SubscribePlanItemCompareRow.vue';
import SubscribePlanItemHeader from './SubscribePlanItemHeader.vue';
import SubscribePlanItemCompareHeader from './SubscribePlanItemCompareHeader.vue';
import SubscribePlanItemCompareCell from './SubscribePlanItemCompareCell.vue';
import SubscribePlansCustomWidget from './SubscribePlansCustomWidget.vue';

const props = defineProps({
  subscriptionPackages: {
    type: Array,
  },
  plansData: {
    type: Array,
  },
  productType: {
    type: String,
  },
})

// Computed
const data = computed(() => props.productType === 'ott'
  ? subscribePlansOttCompareDescription.rows
  : subscribePlansCompareDescription.rows)

const plansDataComputed = computed(() =>{
  if (props.productType === 'ott' ) return [...props.plansData, {name: 'Custom'}]
  return props.plansData
})

const yearly = defineModel('yearly')

// One variant in the DOM at a time (Tailwind's `xl`), not two stacks of 80
// rows with one hidden: the table is heavy, and specs that count the plan
// headers or read their geometry must not meet a display:none twin.
const XL_QUERY = '(min-width: 1280px)'
const xlUp = ref(typeof window !== 'undefined' && window.matchMedia(XL_QUERY).matches)
let xlMq
const syncXlUp = () => { xlUp.value = xlMq.matches }
onMounted(() => {
  xlMq = window.matchMedia(XL_QUERY)
  syncXlUp()
  xlMq.addEventListener('change', syncXlUp)
})
onBeforeUnmount(() => {
  xlMq?.removeEventListener('change', syncXlUp)
})

/*
 * Below `xl` the table shows one plan at a time, chosen from a segmented
 * control above it. A 390px screen fits the feature label and one value
 * column legibly; the old layout put all plans in 50vw columns inside an
 * `overflow-hidden` grid, so everything past the first plan was clipped and
 * unreachable. The label column and the plan column share grid rows, so the
 * price header and every feature row stay aligned for whichever plan is up.
 *
 * Every row carries one value per column of the desktop table, Custom
 * included (desktop shows it under the floating widget), so the live product
 * gets a Custom segment here too; OTT already appends it in plansDataComputed.
 */
const mobilePlans = computed(() => (
  props.productType === 'ott' ? plansDataComputed.value : [...(props.plansData || []), { name: 'Custom' }]
))
const selectedPlanIndex = ref(0)
// plansData is rebuilt on every yearly toggle; keep the chosen segment unless
// it no longer exists.
watch(mobilePlans, (plans) => {
  if (selectedPlanIndex.value >= plans.length) selectedPlanIndex.value = 0
})
const selectedPlan = computed(() => mobilePlans.value[selectedPlanIndex.value] || mobilePlans.value[0])
const planLabel = (plan) => plan?.info?.name || plan?.name || 'Plan'
// Up to five plans share the row equally so every segment is visible on a
// 390px screen; beyond that the group scrolls sideways.
const planTabCols = computed(() => (
  mobilePlans.value.length >= 2 && mobilePlans.value.length <= 5 ? mobilePlans.value.length : null
))
const planTabClass = (active) => [
  'flex h-8 w-full items-center justify-center whitespace-nowrap rounded-md px-1.5 text-tiny font-medium leading-none transition-colors hover:no-underline',
  active ? 'bg-surface-4 text-white shadow-sm' : 'text-surface-8 hover:bg-surface-3 hover:text-white',
].join(' ')
const mobileCell = (row) => {
  const values = (row.dataYearly && yearly.value) ? row.dataYearly : row.data
  if (!Array.isArray(values)) return false
  // A colspan row repeats its single value across every plan.
  return row.colspan ? values[0] : values[selectedPlanIndex.value]
}

</script>

<template>
  <div class="relative" id="compare">
    <!-- Mobile / tablet: pick a plan, compare it against the feature list. -->
    <div v-if="!xlUp" class="xl:hidden" data-testid="compare-mobile">
      <!-- Sticks under the 60px mobile top bar (beside the sidebar from md), so a
           row can be compared across plans without scrolling back up. -->
      <div class="sticky top-[60px] md:top-0 z-20 py-2 bg-surface-1">
        <TabGroup
          type="segmented"
          :cols="planTabCols"
          custom-classes="max-w-full overflow-x-auto no-scrollbar !bg-surface-2 ring-1 ring-inset ring-white/[0.06]"
          aria-label="Plan to compare"
          data-testid="compare-plan-switcher"
        >
          <TabItem
            v-for="(plan, index) in mobilePlans"
            :key="index"
            :label="planLabel(plan)"
            :is-active="index === selectedPlanIndex"
            :custom-classes="planTabClass(index === selectedPlanIndex)"
            @click="selectedPlanIndex = index"
          />
        </TabGroup>
      </div>
      <div
        class="mt-1 grid grid-cols-[minmax(0,45%)_minmax(0,55%)] rounded-xl bg-surface-2 ring-1 ring-surface-3 overflow-hidden text-tiny text-surface-8"
        data-testid="compare-mobile-table"
      >
        <!-- Shares the grid row with SubscribePlanItemHeader, so it takes the
             header's height whatever that is; no height to keep in sync here. -->
        <div class="!p-4 flex flex-col items-start justify-center border-r border-surface-3">
          <template v-if="productType !== 'ott'">
            <FormSwitch class="mb-2" label="Yearly" v-model="yearly" />
            <p class="text-gold font-medium mb-0">Save up to 20% + more concurrent streams</p>
          </template>
        </div>
        <SubscribePlanItemHeader
          v-if="selectedPlan"
          :key="`mobile-plan-${selectedPlanIndex}`"
          class="rounded-tr-xl"
          :plan="selectedPlan"
          :is-yearly="yearly"
          :product-type="productType"
        />
        <!-- Rows grow with their label (the components fix h-12 below lg, which
             clipped long feature names); the grid keeps label and value level. -->
        <template v-for="(row, index) in data" :key="`mobile-row-${index}`">
          <SubscribePlanItemCompareHeader
            class="!h-auto min-h-12 border-r border-surface-3 leading-none text-xs font-medium"
            :class="index % 2 === 0 ? 'bg-surface-1' : 'bg-surface-2'"
            :data="row"
            :is-yearly="yearly"
          />
          <SubscribePlanItemCompareCell
            class="!h-auto min-h-12 font-medium"
            :class="index % 2 === 0 ? 'bg-surface-1' : 'bg-surface-2'"
            :data="mobileCell(row)"
          />
        </template>
      </div>
    </div>

    <!-- Desktop: every plan side by side. -->
    <div v-else class="hidden xl:grid xl:grid-cols-1">
      <div class="xl:w-11/12 2xl:w-10/12 xl:ml-0 2xl:ml-auto 2xl:mr-auto">
        <div
          class="grid grid-cols-[repeat(5,50vw)] xl:grid-cols-[20%_repeat(4,16%)] rounded-xl mx-auto divide-x divide-surface-3 xl:w-full bg-surface-2 overflow-hidden ring-1 ring-surface-3">
          <div
            class="!p-4 text-tiny text-surface-8 bg-surface-2 flex-col items-start justify-center size-full rounded-tl-xl md:sticky top-0 z-10 !hidden xl:!flex"
            id="bottom">
            <template v-if="productType !== 'ott'">
              <FormSwitch class="mb-2" label="Yearly" v-model="yearly" />
            <p class="text-gold font-medium">{{ productType === 'ott' ? 'Save up to 20%' : 'Save up to 20% + more concurrent streams' }}</p>
            </template>
          </div>
          <SubscribePlanItemHeader class="last-of-type:rounded-tr-xl sticky top-0" :plan="plan"
            v-for="(plan, index) in plansDataComputed" :key="index" :is-yearly="yearly"  :product-type="productType" />
          <SubscribePlanItemCompareRow v-for="(row, index) in data" :key="index" :row="row" :is-yearly="yearly" :product-type="productType" />
        </div>
      </div>
    </div>
    <SubscribePlansCustomWidget class="absolute left-[87%] top-20 !hidden xl:!block" />
  </div>
</template>

<style></style>
